import posthog from 'posthog-js';

import type { WebMcpTool } from '@/hooks/use-webmcp-tool';
import { SCANNER_COPY } from '@/lib/content';
import { EMAIL_REGEX, URL_REGEX } from '@/lib/validation';

/**
 * The readiness scan, offered to a browser agent. The email is required for the same reason the
 * form asks: not to protect the scores, which are a pure function of the domain, but so the one
 * path a lead arrives by has no quieter second entrance.
 */
export const scanBrandTool: WebMcpTool = {
  name: 'scanBrand',
  description: SCANNER_COPY.toolDescription,
  inputSchema: {
    type: 'object',
    properties: {
      domain: { type: 'string', description: SCANNER_COPY.toolDomainDescription },
      email: { type: 'string', description: SCANNER_COPY.toolEmailDescription },
    },
    required: ['domain', 'email'],
  },
  // Files a lead: an action, not a lookup, so an agent confirms before calling it.
  annotations: { readOnlyHint: false },
  async execute({ domain, email }: { domain: string; email: string }, { signal }) {
    const cleanDomain = domain.trim();
    const cleanEmail = email.trim();
    if (!URL_REGEX.test(cleanDomain)) return SCANNER_COPY.toolNotAWebsite;
    if (!EMAIL_REGEX.test(cleanEmail)) return SCANNER_COPY.toolNotAnEmail;

    // Imported here, not at the top: this tool is registered on every page the scan CTA appears on,
    // and the scoring tables are several KB that no visitor without WebMCP will ever run.
    const { scoreDomain, submitScanReport } = await import('@/lib/scan-score');
    const scored = scoreDomain(cleanDomain);
    void submitScanReport({
      scored,
      email: cleanEmail,
      source: 'webmcp',
      distinctId: posthog.get_distinct_id?.(),
      signal,
    });

    const dims = scored.dims
      .map(dim => `- ${dim.name} ${dim.score}/100 — ${dim.tag}. ${dim.finding}`)
      .join('\n');
    return `${scored.domain}: ${scored.overall}/100, ${scored.tier}.\n${scored.verdict}\n\n${dims}`;
  },
};
